import { suppress } from './chime.ts';
import { enqueue, flush } from './outbox.ts';
/**
 * Every call is same-origin and credentialed.
 *
 * The session cookie is HttpOnly, so it is never read in JS -- the
 * browser attaches it. Device access tokens are the other credential
 * shape and are passed explicitly.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

/**
 * The desktop shell owns the device token and injects it before the
 * page loads. In a browser this is always undefined and the session
 * cookie carries auth instead, so one client serves both.
 */
declare global {
  interface Window {
    __KESTRELAY_TOKEN__?: string;
  }
}

/**
 * The shell injects its token on the window and rotates it every
 * fifteen minutes, so that always wins. A phone enrolled as a site
 * device has no shell: its credential went to localStorage and was
 * then read by nothing, so a PWA could enrol and still never
 * authenticate.
 */
export const deviceToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  if (window.__KESTRELAY_TOKEN__) return window.__KESTRELAY_TOKEN__;
  try {
    return localStorage.getItem('kr.device') ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Paths whose failure should queue rather than surface an error.
 *
 * Only things a site TYPES. A failed read can simply be retried by the
 * next poll, but a message someone composed is gone unless it is kept.
 */
const QUEUEABLE = ['/messages', '/requests', '/chat/rooms'];

export const authHeaders = (): Record<string, string> => {
  const token = deviceToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const request = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  // Anything we post comes straight back down the socket, and the
  // event carries no sender, so the chime is muted here rather than
  // guessed at on arrival.
  if (init.method === 'POST' && QUEUEABLE.some((q) => path.startsWith(q))) {
    suppress();
  }
  let response: Response;
  try {
    response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      // Only declare a JSON body when there is one. Fastify rejects an
      // empty body with this content-type before routing, which turned
      // every bodyless POST -- revoke, archive, mark-opened, logout --
      // into a generic client-side failure.
      ...(init.body === undefined
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...init.headers,
    },
      ...init,
    });
  } catch (networkError) {
    // The line is down. If this was something a person typed, keep
    // it and say so, rather than losing it silently.
    const queueable =
      init.method === 'POST' &&
      typeof init.body === 'string' &&
      QUEUEABLE.some((p) => path.startsWith(p));

    if (queueable) {
      enqueue(path, init.body as string);
      throw new ApiError(0, 'queued_offline');
    }
    throw networkError;
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(response.status, body.error ?? 'unknown_error');
  }
  return body as T;
};

export interface Branding {
  readonly slug: string;
  /** Hide everything but chat. Server refuses regardless. */
  readonly chatOnly?: boolean;
  readonly publicIp?: string | null;
  readonly userId?: string | null;
  readonly isAdmin?: boolean;
  readonly name: string;
  readonly identity?: {
    product_name?: string;
    short_name?: string;
    logo_version?: number;
    logo_plate?: boolean;
  };
  readonly theme?: { primary?: string };
  readonly locale?: { timezone?: string };
}

/**
 * /auth/login either challenges for a second factor or signs you in
 * outright -- the demo tenant and a browser inside its trust window
 * take the second path, and come back with neither field set.
 */
export interface LoginChallenge {
  readonly totpRequired?: boolean;
  readonly challenge?: string;
}

export interface Message {
  readonly id: string;
  /** Which site it belongs to. Shown when several are on screen. */
  readonly site_name?: string;
  /** Changes when a file is attached after the message was
      sent, which is what makes a counter screen refetch. */
  readonly attachment_count?: number;
  readonly seq: string;
  readonly kind: 'message' | 'notice' | 'system';
  readonly body: string;
  readonly priority: 'normal' | 'urgent';
  readonly requires_ack: boolean;
  readonly pinned_until: string | null;
  readonly created_at: string;
  readonly sender_user_name: string | null;
  readonly sender_device_name: string | null;
  /** 1 = this site only. More = the same notice went to that many. */
  readonly sent_to_sites: number;
  /** This caller's own receipt, so the button can show state. */
  readonly opened_at: string | null;
  readonly acked_at: string | null;
  readonly category_name: string;
  readonly category_color: string;
}

export interface CategoryBadge {
  readonly categoryId: string;
  readonly slug: string;
  readonly name: string;
  readonly icon: string;
  readonly color: string;
  readonly position: number;
  readonly count: number;
}

export interface BadgeSummary {
  readonly categories: readonly CategoryBadge[];
  readonly total: number;
  readonly trayLabel: string;
  /** Which screen this is. Null for accounts. */
  readonly deviceName: string | null;
  /** The shell build this screen is running, when it is one. */
  readonly appVersion?: string | null;
  /** Unread notices from HQ. Excludes requests and own messages. */
  readonly unreadNotices: number;
  /** Requests where HQ answered and the site has not replied. */
  readonly repliesWaiting: number;
  /** Chat rooms where staff spoke last. */
  readonly chatWaiting: number;
}

export const api = {
  branding: () => request<Branding>('/api/branding'),

  login: (email: string, password: string) =>
    request<LoginChallenge>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  totp: (challenge: string, code: string, trust = false) =>
    request<{ ok: true }>('/auth/totp', {
      method: 'POST',
      body: JSON.stringify({ challenge, code, trust }),
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
};

export const boardApi = {
  badges: () => request<BadgeSummary>('/badges'),

  // categoryId null means every category the caller can see.
  messages: (siteId: string | null, categoryId: string | null,
            archived = false) =>
    request<Message[]>(
      `/messages?${siteId ? 'siteId=' + siteId : ''}` +
        (categoryId ? `&categoryId=${categoryId}` : '') +
        (archived ? '&archived=true' : ''),
    ),

  /**
   * A deliberate act. Never called from a visibility or focus handler --
   * an always-on counter screen would mark everything read on render
   * and silently destroy read coverage.
   */
  open: (messageId: string) =>
    request<void>(`/messages/${messageId}/open`, { method: 'POST' }),

  ack: (messageId: string, staffId: string) =>
    request<void>(`/messages/${messageId}/ack`, {
      method: 'POST',
      body: JSON.stringify({ staffId }),
    }),
};

export interface Site {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly timezone: string;
  /** "HH:MM". These gate push delivery, so they are not cosmetic. */
  readonly opens_at?: string;
  readonly closes_at?: string;
  readonly max_devices?: number;
}

export interface Category {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly icon: string;
  readonly color: string;
  readonly position: number;
  readonly allows_site_initiated: boolean;
  readonly requires_ack: boolean;
}

export const directory = {
  sites: () => request<Site[]>('/sites'),
  categories: () => request<Category[]>('/categories'),
};

export interface SendMessage {
  readonly siteId: string;
  readonly categoryId: string;
  readonly body: string;
  readonly kind?: 'message' | 'notice';
  readonly priority?: 'normal' | 'urgent';
  readonly requiresAck?: boolean;
}

/**
 * clientRef is generated here, once per send, and survives every
 * retry including one replayed from the offline queue. Without it a
 * request that timed out after the server had already committed came
 * back as a second notice on the board.
 */
export const sendMessage = (message: SendMessage) =>
  request<{ id: string; seq: string }>('/messages', {
    method: 'POST',
    body: JSON.stringify({
      clientRef: crypto.randomUUID(),
      ...message,
    }),
  });

export type SetupStage =
  | { readonly stage: 'confirm_totp'; readonly uri: string }
  | { readonly stage: 'complete' };

export const setup = {
  begin: (token: string, password: string) =>
    request<SetupStage>('/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  confirm: (token: string, password: string, code: string) =>
    request<SetupStage>('/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ token, password, code }),
    }),
};

export interface SiteCoverage {
  readonly siteId: string;
  readonly siteName: string;
  readonly openedAt: string | null;
  readonly openedOn: string | null;
  readonly ackedAt: string | null;
  readonly ackedBy: string | null;
}

export interface CoverageReport {
  readonly broadcastId: string;
  readonly body: string;
  readonly requiresAck: boolean;
  readonly sentAt: string;
  readonly sentTo: number;
  readonly opened: number;
  readonly acked: number;
  readonly sites: readonly SiteCoverage[];
}

export interface BroadcastRow {
  readonly id: string;
  readonly body: string;
  readonly requires_ack: boolean;
  readonly priority: string;
  readonly site_count: number;
  readonly opened: string;
  readonly category: string;
  readonly created_at: string;
}

export interface DeviceRow {
  readonly deviceId: string;
  readonly deviceName: string;
  readonly siteName: string;
  readonly appVersion: string | null;
  readonly minutesSince: number | null;
  readonly state: 'online' | 'idle' | 'stale' | 'never';
}

export const reports = {
  broadcasts: () => request<BroadcastRow[]>('/reports/broadcasts'),
  coverage: (id: string) => request<CoverageReport>(`/reports/coverage/${id}`),
  devices: () =>
    request<{ devices: DeviceRow[] }>('/reports/devices'),
};

export const sendBroadcast = (payload: {
  categoryId: string;
  siteIds: string[];
  body: string;
  priority?: 'normal' | 'urgent';
  requiresAck?: boolean;
  /** Hours to keep pinned above the conversation. 0 = not pinned. */
  pinHours?: number;
}) =>
  request<{ broadcastId: string; messageIds: string[];
           siteCount: number }>('/broadcasts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export interface AdminDevice {
  readonly id: string;
  readonly name: string;
  readonly app_version: string | null;
  readonly last_seen_at: string | null;
  readonly minutes_since: string | null;
  readonly last_ip: string | null;
}

export interface PendingCode {
  readonly id: string;
  readonly code: string;
  readonly device_name: string | null;
  readonly seconds_left: string;
}

export interface SiteDetail {
  readonly site: { name: string; max_devices: number };
  readonly devices: readonly AdminDevice[];
  readonly pendingCodes: readonly PendingCode[];
  readonly slotsLeft: number;
}

/** One weekday of a site's schedule, as the grid needs it: what is
    stored, and what a blank day resolves to. */
export interface SiteDayHours {
  readonly dow: number;
  readonly has_row: boolean;
  readonly closed: boolean;
  readonly opens_at: string | null;
  readonly closes_at: string | null;
  readonly effective_opens: string | null;
  readonly effective_closes: string | null;
}

export interface SiteHoursWrite {
  readonly dow: number;
  readonly opensAt?: string | null;
  readonly closesAt?: string | null;
  readonly closed?: boolean;
}

export const admin = {
  siteDetail: (siteId: string) =>
    request<SiteDetail>(`/admin/sites/${siteId}/devices`),

  hours: (siteId: string) =>
    request<{ days: readonly SiteDayHours[] }>(
      `/admin/sites/${siteId}/hours`),

  /** The whole week every time. A day left out of the list has no row
      and therefore inherits, which is what the grid's "Default"
      state means. */
  saveHours: (siteId: string, days: readonly SiteHoursWrite[]) =>
    request<{ ok: boolean; days: number }>(
      `/admin/sites/${siteId}/hours`,
      { method: 'PUT', body: JSON.stringify({ days }) }),

  /** Hours and timezone had no update route at all, so every site
      was stuck on the creation defaults and push was held outside
      08:00-18:00 America/New_York everywhere. */
  updateSite: (
    id: string,
    payload: {
      name?: string;
      timezone?: string;
      opensAt?: string;
      closesAt?: string;
      maxDevices?: number;
    },
  ) =>
    request<Site>(`/admin/sites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  createSite: (payload: {
    name: string;
    code?: string | null;
    maxDevices?: number;
  }) =>
    request<Site>('/admin/sites', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  issueCode: (siteId: string, deviceName: string | null, hours = 1) =>
    request<PendingCode>('/admin/enrolment-codes', {
      method: 'POST',
      body: JSON.stringify({ siteId, deviceName, hours }),
    }),

  revokeDevice: (deviceId: string) =>
    request<{ revoked: string; name: string }>(
      `/admin/devices/${deviceId}/revoke`,
      { method: 'POST' },
    ),

  addStaff: (siteId: string, name: string, pin: string) =>
    request<{ id: string; name: string }>('/admin/staff', {
      method: 'POST',
      body: JSON.stringify({ siteId, name, pin }),
    }),
};

export interface PinnedNotice {
  readonly id: string;
  readonly body: string;
  readonly priority: 'normal' | 'urgent';
  readonly requires_ack: boolean;
  readonly pinned_until: string;
  readonly category: string;
  readonly color: string;
  readonly sender: string | null;
  readonly opened_at: string | null;
  readonly acked_at: string | null;
  readonly acked_by: string | null;
}

export const pinned = (siteId?: string) =>
  request<PinnedNotice[]>(
    siteId ? `/notices/pinned?siteId=${siteId}` : '/notices/pinned',
  );

export type RequestStatus =
  | 'open' | 'acknowledged' | 'in_progress' | 'resolved' | 'closed';

export interface SiteRequest {
  readonly id: string;
  readonly title: string;
  readonly status: RequestStatus;
  readonly priority: 'normal' | 'urgent';
  readonly opened_at: string;
  readonly first_response_at: string | null;
  readonly resolved_at: string | null;
  readonly resolution_note: string | null;
  readonly site_name: string;
  readonly category: string;
  readonly color: string;
  readonly assignee: string | null;
  readonly assignee_user_id?: string | null;
  readonly opened_on: string | null;
  /** Staff name when the filer entered a PIN. */
  readonly opened_by: string | null;
  readonly replies: number;
  /** Split by side: a total tells neither party what is new. */
  readonly from_hq: number;
  readonly from_site: number;
}

export const requests = {
  list: (openOnly = false, siteId?: string) => {
    const q = new URLSearchParams();
    if (openOnly) q.set('open', '1');
    if (siteId) q.set('siteId', siteId);
    const s = q.toString();
    return request<SiteRequest[]>(`/requests${s ? `?${s}` : ''}`);
  },

  file: (payload: {
    categoryId: string;
    title: string;
    body: string;
    priority?: 'normal' | 'urgent';
  }) =>
    request<{ id: string; status: RequestStatus; messageId: string }>('/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  setStatus: (
    id: string,
    status: RequestStatus,
    note?: string,
    assignToMe = false,
  ) =>
    request<{ status: RequestStatus }>(`/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, note, assignToMe }),
    }),
};

export const invite = {
  begin: (userId: string, token: string, password: string) =>
    request<SetupStage>('/auth/invite', {
      method: 'POST',
      body: JSON.stringify({ userId, token, password }),
    }),

  confirm: (
    userId: string,
    token: string,
    password: string,
    code: string,
  ) =>
    request<SetupStage>('/auth/invite', {
      method: 'POST',
      body: JSON.stringify({ userId, token, password, code }),
    }),
};

export interface Supervisor {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly deactivated_at: string | null;
  readonly pending_setup: boolean;
  readonly role?: 'admin' | 'supervisor';
  /** The API has always returned this; nothing read it. */
  readonly chat_only?: boolean;
  readonly is_account_owner?: boolean;
  readonly sites: readonly string[];
  readonly categories: readonly string[];
}

export interface NewSupervisor extends Supervisor {
  readonly inviteUrl: string;
  readonly coverage: { sites: number; categories: number };
}

export const supervisors = {
  list: () => request<Supervisor[]>('/admin/supervisors'),

  create: (payload: {
    email: string;
    name: string;
    chatOnly?: boolean;
    /** 'admin' is full access; scope lists do not apply. */
    role?: 'admin' | 'supervisor';
    siteIds: string[];
    categoryIds: string[];
  }) =>
    request<NewSupervisor>('/admin/supervisors', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  makeOwner: (id: string) =>
    request<{ owner: string }>(
      `/admin/users/${id}/make-owner`,
      { method: 'POST' },
    ),

  revokeTrust: (id: string) =>
    request<{ revoked: number }>(
      `/admin/supervisors/${id}/revoke-trust`,
      { method: 'POST' },
    ),

  /** chatOnly and name ride along: the edit screen offers both, and
      sending only the scope silently reverted chat-only to false. */
  setScope: (
    id: string,
    siteIds: string[],
    categoryIds: string[],
    extra: { chatOnly?: boolean; name?: string } = {},
  ) =>
    request<{ updated: string; coverage: { sites: number;
      categories: number } }>(`/admin/supervisors/${id}/scope`, {
      method: 'PUT',
      body: JSON.stringify({ siteIds, categoryIds, ...extra }),
    }),
};

export interface OverdueRequest {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  readonly priority: string;
  readonly siteName: string;
  readonly category: string;
  readonly slaHours: number | null;
  readonly hoursOpen: number;
  readonly overdueBy: number;
  readonly assignee: string | null;
}

export interface UncoveredCategory {
  readonly id: string;
  readonly name: string;
  readonly open_requests: number;
  readonly pending: number;
  readonly supervisors: number;
}

/** The two waiting counts for an account, since /badges is
    device-only. */
export const counts = () =>
  request<{ chatWaiting: number; repliesWaiting: number }>(
    '/counts',
  );

/** Hand a ticket to someone, or null to return it to the queue. */
export const assignRequest = (id: string, userId: string | null) =>
  request<{ assigned: string | null }>(`/requests/${id}/assign`, {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });

export const assignable = () =>
  request<Array<{ id: string; name: string }>>(
    '/requests/assignable',
  );

export const overdue = () =>
  request<{
    requests: OverdueRequest[];
    uncovered: UncoveredCategory[];
  }>('/reports/overdue');

export interface Attachment {
  readonly id: string;
  readonly filename: string;
  readonly mime: string;
  readonly bytes: string;
  readonly has_thumb: boolean;
}

export interface SignedFile {
  readonly url: string;
  readonly expiresIn: number;
  readonly filename: string;
  readonly mime: string;
}

export const files = {
  list: (messageId: string) =>
    request<Attachment[]>(`/messages/${messageId}/attachments`),

  /**
   * Signed links are requested at click time, never held in state. They
   * live 60 seconds, so a URL prefetched on render is dead before the
   * user gets to it.
   */
  sign: (id: string, variant: 'full' | 'thumb' = 'full') =>
    request<SignedFile>(`/files/${id}?variant=${variant}`),
};

export type UploadFailure =
  | 'unsupported_type'
  | 'too_large'
  | 'corrupt_image'
  | 'no_file'
  | 'unknown';

/**
 * Multipart upload. Content-Type is set by the browser from the
 * FormData boundary, so it must not be overridden here.
 */
export const uploadAttachment = async (
  messageId: string,
  file: File,
): Promise<{ ok: true } | { ok: false; reason: UploadFailure }> => {
  const form = new FormData();
  form.append('file', file);

  // Content-Type is omitted deliberately: the browser must set it from
  // the FormData boundary. Auth still has to be attached by hand
  // because this path does not go through request().
  const response = await fetch(`/messages/${messageId}/attachments`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: authHeaders(),
    body: form,
  });

  if (response.ok) return { ok: true };

  const body = await response.json().catch(() => ({}));
  return { ok: false, reason: (body.error ?? 'unknown') as UploadFailure };
};

export const handoff = {
  issue: (messageId: string) =>
    request<{ token: string; url: string; expiresIn: number }>(
      `/messages/${messageId}/handoff`,
      { method: 'POST' },
    ),
};

/**
 * Unauthenticated by design: the token in the path is the whole
 * authorisation. Requiring a login would defeat handing a QR to whoever
 * is standing in front of the broken thing.
 */
export const uploadViaHandoff = async (
  token: string,
  file: File,
): Promise<{ ok: boolean; reason?: string }> => {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`/photo/${token}/upload`, {
    method: 'POST',
    body: form,
  });

  if (response.ok) return { ok: true };
  const body = await response.json().catch(() => ({}));
  return { ok: false, reason: body.error ?? 'unknown' };
};

export interface CategoryInput {
  readonly name: string;
  readonly slug: string;
  readonly icon?: string;
  readonly color?: string;
  readonly allowsSiteInitiated?: boolean;
  readonly requiresAck?: boolean;
  readonly slaHours?: number | null;
}

export const categoriesAdmin = {
  create: (payload: CategoryInput) =>
    request<Category>('/admin/categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: Partial<CategoryInput>) =>
    request<Category>(`/admin/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  archive: (id: string) =>
    request<{ archived: string }>(`/admin/categories/${id}/archive`, {
      method: 'POST',
    }),
};

export interface TenantSettings {
  /** How the desktop shell sits on a counter screen. */
  readonly display?: {
    dock?: 'off' | 'left' | 'right';
    /** Panel width in logical pixels. */
    dockWidth?: number;
  };
  readonly identity?: {
    product_name?: string;
    short_name?: string;
    logo_version?: number;
    logo_plate?: boolean;
  };
  readonly theme?: { primary?: string };
  readonly locale?: { timezone?: string; date_format?: string };
  readonly board?: { window_days?: number };
  readonly hours?: {
    opens_at?: string;
    closes_at?: string;
    quiet_hours?: boolean;
  };
}

export const uploadLogo = async (file: File): Promise<void> => {
  const form = new FormData();
  form.append('file', file);
  const r = await fetch('/admin/branding/logo', {
    method: 'POST',
    credentials: 'same-origin',
    headers: authHeaders(),
    body: form,
  });
  if (!r.ok) throw new Error(String(r.status));
};

export const restoreMessage = (id: string) =>
  request<{ restored: string }>(`/messages/${id}/restore`, {
    method: 'POST',
  });

export const archiveMessage = (id: string) =>
  request<{ archived: string }>(`/messages/${id}/archive`, {
    method: 'POST',
  });

export const settings = {
  get: () => request<TenantSettings>('/admin/settings'),
  patch: (payload: TenantSettings) =>
    request<TenantSettings>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
};

export interface SearchHit {
  readonly id: string;
  readonly body: string;
  readonly createdAt: string;
  readonly siteName: string;
  readonly category: string;
  readonly color: string;
  readonly sender: string | null;
  readonly kind: string;
  readonly requestId: string | null;
  readonly requestTitle: string | null;
  readonly siteId: string;
  readonly categoryId: string;
}

export const search = (q: string) =>
  request<SearchHit[]>(`/search?q=${encodeURIComponent(q)}`);

export interface ThreadEntry {
  readonly id: string;
  readonly body: string;
  readonly createdAt: string;
  readonly author: string;
  readonly fromSite: boolean;
  readonly signed?: boolean;
}

export const thread = {
  read: (requestId: string) =>
    request<ThreadEntry[]>(`/requests/${requestId}/thread`),

  reply: (requestId: string, body: string, pin?: string) =>
    request<{ id: string }>(`/requests/${requestId}/replies`, {
      method: 'POST',
      body: JSON.stringify(pin ? { body, pin } : { body }),
    }),
};

export type AckResult =
  | { readonly ok: true; readonly acknowledgedBy: string }
  | {
      readonly ok: false;
      readonly reason: string;
      readonly remaining?: number;
      readonly retryAfter?: number;
    };

/**
 * Acknowledge with a staff PIN. Never sends a staff id: the PIN is
 * what proves who is standing there, and accepting an id would let
 * any device sign as anyone at its site.
 */
export const acknowledge = async (
  messageId: string,
  pin: string,
): Promise<AckResult> => {
  const r = await fetch(`/messages/${messageId}/ack`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ pin }),
  });

  const data = await r.json().catch(() => ({}));
  if (r.ok) return { ok: true, acknowledgedBy: data.acknowledgedBy };
  return { ok: false, reason: 'invalid_pin', ...data };
};

export interface ChatMessage {
  readonly id: string;
  readonly seq: string;
  readonly body: string;
  readonly author: string;
  readonly fromSite: boolean;
  readonly createdAt: string;
  readonly files: string[];
}

export interface ChatRoom {
  readonly id: string;
  readonly siteId: string;
  readonly siteName: string;
  readonly categoryId: string;
  readonly categoryName: string;
  /** Who the other side is, when a person has spoken. */
  readonly withName?: string | null;
  readonly color: string;
  readonly openedAt: string;
  readonly lastAt: string;
  readonly closedAt: string | null;
  readonly messageCount: number;
  /** The site spoke last: this room is waiting on you. */
  readonly waiting: boolean;
  /** Site messages since the last staff reply. */
  readonly unread: number;
  /** Which trading day this room is filed under, YYYY-MM-DD. */
  readonly businessDay: string | null;
}

export interface ChatDay {
  readonly day: string;
  readonly messages: number;
  /** The day still being written to. */
  readonly live: boolean;
}

export interface RoomState {
  readonly sessionId: string;
  readonly messages: ChatMessage[];
  readonly staffPresent: string[];
}

export const chat = {
  rooms: () => request<ChatRoom[]>('/chat/rooms'),

  open: (categoryId: string, siteId?: string) =>
    request<RoomState>('/chat/rooms', {
      method: 'POST',
      body: JSON.stringify(siteId ? { siteId, categoryId } : { categoryId }),
    }),

  send: (sessionId: string, body: string, pin?: string) =>
    request<{ id: string; seq: string }>(
      `/chat/rooms/${sessionId}/messages`,
      { method: 'POST', body: JSON.stringify(pin ? { body, pin } : { body }) },
    ),

  // `since` is the client's highest seq: on reconnect it asks for the
  // gap rather than refetching a whole conversation over retail wifi.
  poll: (sessionId: string, since?: string) =>
    request<Omit<RoomState, 'sessionId'>>(
      `/chat/rooms/${sessionId}/messages` +
        (since ? `?since=${since}` : ''),
    ),

  history: (f: {
    siteId?: string;
    categoryId?: string;
    from?: string;
    to?: string;
    day?: string;
    text?: string;
  }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v) q.set(k, v);
    return request<ChatRoom[]>(`/chat/history?${q.toString()}`);
  },

  /** The trading days this store has a transcript for, newest first. */
  days: (siteId: string) =>
    request<{ days: ChatDay[] }>(
      `/chat/days?siteId=${encodeURIComponent(siteId)}`),

  attach: async (messageId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    const r = await fetch(`/chat/messages/${messageId}/attachments`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: authHeaders(),
      body: form,
    });
    if (!r.ok) throw new Error(String(r.status));
  },

  escalate: (sessionId: string, title: string) =>
    request<{ requestId: string }>(
      `/chat/rooms/${sessionId}/escalate`,
      { method: 'POST', body: JSON.stringify({ title }) },
    ),

  close: (sessionId: string) =>
    request<{ closed: string }>(`/chat/rooms/${sessionId}/close`, {
      method: 'POST',
    }),
};

/**
 * Send anything that queued during an outage.
 *
 * Called when the connection returns. Uses the same headers as a live
 * request, so a device token queued before a refresh still authenticates
 * after one.
 */
export const flushOutbox = () =>
  flush((path, body) =>
    fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body,
    }),
  );

export { queued } from './outbox.ts';
